import { cloneDeep, capitalize } from 'lodash'
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import UsersSelect from 'live/components/forms/ui/users-select'
import i18n from 'live/services/i18n'
import { AddRowButton, RowRemoveButton, Asterisk } from '../common/components'
import { userFilter } from '../common/utils'
import LiveTooltip from 'live/components/ui/tooltip'
import { EmptyFields } from './analysisTypes'

interface IProps {
    generalInfo: GeneralInfo
    setGeneralInfo: React.Dispatch<React.SetStateAction<GeneralInfo>>
    emptyFields: EmptyFields
    setEmptyFields: (emptyFields: EmptyFields) => void
}

export function ParticipantsInfo({ generalInfo, setGeneralInfo, emptyFields, setEmptyFields }: IProps): JSX.Element {
    const [participantsInputState, setParticipantsInputState] = useState('')

    useEffect(() => {
        if (emptyFields.fields.includes('participants')) {
            setParticipantsInputState('Alert')
        }
    }, [emptyFields, emptyFields.fields, emptyFields.trigger])

    const cleanParticipantsInputStatus = (): void => {
        const localEmptyFields = emptyFields
        localEmptyFields.trigger = false
        localEmptyFields.fields = localEmptyFields.fields.filter(field => {
            return field !== 'participants'
        })
        setEmptyFields(localEmptyFields)
        setParticipantsInputState('')
    }

    function onChangeParticipants(index: number, newUserData: UserData): void {
        cleanParticipantsInputStatus()
        const updatedInfo = cloneDeep(generalInfo)
        updatedInfo.participants[index] = newUserData
        setGeneralInfo(updatedInfo)
    }

    function onAddParticipant(event): void {
        cleanParticipantsInputStatus()
        event.preventDefault()
        const updatedInfo = cloneDeep(generalInfo)
        updatedInfo.participants.push(null)
        setGeneralInfo(updatedInfo)
    }

    function onRemoveParticipant(event, index): void {
        cleanParticipantsInputStatus()
        event.preventDefault()
        const updatedInfo = cloneDeep(generalInfo)
        updatedInfo.participants.splice(index, 1)
        setGeneralInfo(updatedInfo)
    }

    return (
        <StyledParticipantsInfoEditing>
            <h1 className={`participantsInfo--title${participantsInputState}`}>
                {i18n('participants')}{' '}
                <LiveTooltip position="top" content={i18n('Analysis participants')} hide={false}>
                    <span className="participantsInfo--title__info">
                        <i className="far fa-info-circle"></i>
                    </span>
                </LiveTooltip>
                <Asterisk />
            </h1>
            {generalInfo.participants.map((participant, index) => (
                <div className="participantsInfo--participant" key={index}>
                    <UsersSelect
                        minCharQuery="1"
                        onChange={userData => {
                            const { value, label } = userData
                            onChangeParticipants(index, userData ? { value, label } : null)
                        }}
                        filter={user => userFilter(user, generalInfo.participants)}
                        className="participantsInfo--participant__input"
                        selected={participant}
                        placeholder={capitalize(i18n("participant's name"))}
                    />
                    <RowRemoveButton
                        className="participantsInfo--participant__remove"
                        onClick={event => onRemoveParticipant(event, index)}
                    >
                        <i className="far fa-trash-alt" />
                    </RowRemoveButton>
                </div>
            ))}
            <AddRowButton className="participantsInfo--addButton" onClick={event => onAddParticipant(event)}>
                <i className="fas fa-plus" /> {i18n('add participant')}
            </AddRowButton>
        </StyledParticipantsInfoEditing>
    )
}

const StyledParticipantsInfoEditing = styled.div`
    --border: 1px solid ${({ theme }) => theme['gray-400']};
    width: 100%;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    align-self: start;
    border: var(--border);

    .Select-control {
        border: none;
    }
    .participantsInfo {
        &--titleAlert {
            background-color: #fdd0cf;
        }
        &--titleAlert,
        &--title {
            width: 100%;
            padding: 12px 24px;
            text-transform: uppercase;
            font-family: ${({ theme }) => theme['font-semibold']};
            color: ${({ theme }) => theme['gray-800']};
            border-bottom: var(--border);
            font-size: 16px;
            line-height: 16px;
            &__info {
                color: #37a3ef;
                font-size: 14px;
            }
        }
        &--participant {
            display: flex;
            align-items: center;
            border-bottom: var(--border);
            &__input {
                border: 1px solid transparent;
                font-size: 14px;
                width: 100%;
                .Select-control {
                    cursor: pointer;
                    box-shadow: none;
                    border: none;
                }
                .Select-arrow {
                    display: none;
                }
                &:focus,
                &:focus-within {
                    border-color: ${({ theme }) => theme['blue-500']};
                }
            }
        }
    }
`
